<template>
  <div class="ic-page py-3">
    <header class="ic-toolbar">
      <div class="ic-toolbar__title">
        <span class="ic-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Info Center</h1>
          <p>Employee policies, SOPs and role guides — written once, read by everyone who needs them.</p>
        </div>
      </div>
      <div class="ic-toolbar__actions">
        <button
          v-if="canEdit"
          type="button"
          class="ic-btn ic-btn--primary"
          @click="goToCreate"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Document
        </button>
        <button
          type="button"
          class="ic-btn ic-btn--ghost ic-refresh"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadDocuments"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="ic-refresh__icon">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
          </svg>
          {{ isLoading ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="ic-alert">{{ errorMessage }}</CAlert>

    <!-- Compact filter bar: search + status toggle on one row -->
    <section class="ic-bar">
      <div class="ic-search">
        <svg class="ic-search__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7"/>
          <line x1="20" y1="20" x2="16.65" y2="16.65"/>
        </svg>
        <input
          v-model="search"
          type="search"
          placeholder="Search policies, SOPs, guides…"
          class="ic-search__input"
          @keydown.enter.prevent
        />
        <button v-if="search" type="button" class="ic-search__clear" @click="search = ''" aria-label="Clear search">×</button>
      </div>

      <div v-if="canEdit && organisations.length" class="ic-org-filter">
        <AppDropdown
          :value="orgFilter"
          :options="orgFilterOptions"
          placeholder="All organisations"
          :searchable="organisations.length > 8"
          search-placeholder="Search organisations"
          @input="(val) => (orgFilter = Number(val) || 0)"
        />
      </div>

      <div v-if="canEdit" class="ic-status-toggle" role="group" aria-label="Status filter">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          type="button"
          class="ic-status-btn"
          :class="{ 'is-active': statusFilter === opt.value }"
          @click="statusFilter = opt.value"
        >{{ opt.label }}</button>
      </div>

      <div class="ic-view-toggle" role="group" aria-label="View mode">
        <button
          type="button"
          class="ic-view-btn"
          :class="{ 'is-active': viewMode === 'grid' }"
          @click="setViewMode('grid')"
          title="Card view"
          aria-label="Card view"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
        </button>
        <button
          type="button"
          class="ic-view-btn"
          :class="{ 'is-active': viewMode === 'list' }"
          @click="setViewMode('list')"
          title="List view"
          aria-label="List view"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
        </button>
      </div>
    </section>

    <!-- Category chip strip -->
    <section class="ic-chipbar-wrap">
      <div class="ic-chipbar" role="tablist" aria-label="Categories">
        <button
          type="button"
          role="tab"
          :aria-selected="activeCategory === 'all'"
          class="ic-chip"
          :class="{ 'is-active': activeCategory === 'all' }"
          @click="activeCategory = 'all'"
        >
          <span class="ic-chip__icon">{{ categoryIcon('all') }}</span>
          <span>All</span>
          <span class="ic-chip__count">{{ documents.length }}</span>
        </button>
        <button
          v-for="cat in categoryList"
          :key="cat.key"
          type="button"
          role="tab"
          :aria-selected="activeCategory === cat.key"
          class="ic-chip"
          :class="{ 'is-active': activeCategory === cat.key }"
          @click="activeCategory = cat.key"
        >
          <span class="ic-chip__icon">{{ categoryIcon(cat.key) }}</span>
          <span>{{ cat.label }}</span>
          <span class="ic-chip__count">{{ cat.count }}</span>
        </button>
      </div>

      <div class="ic-stat-line">
        <span><strong>{{ totalCount }}</strong> docs</span>
        <span class="ic-stat-line__dot" aria-hidden="true">·</span>
        <span><strong>{{ pinnedCount }}</strong> pinned</span>
        <template v-if="canEdit">
          <span class="ic-stat-line__dot" aria-hidden="true">·</span>
          <span><strong>{{ draftCount }}</strong> drafts</span>
        </template>
      </div>
    </section>

    <!-- Pinned section (cards only — list view shows pinned inline) -->
    <section v-if="pinnedDocs.length && viewMode === 'grid'" class="ic-section">
      <div class="ic-section__head">
        <span class="ic-section__icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.39 4.84L20 7.62l-3.91 3.81.92 5.37L12 14.27 6.99 16.8l.92-5.37L4 7.62l5.61-.78L12 2z"/></svg>
        </span>
        <h2>Pinned</h2>
        <span class="ic-section__count">{{ pinnedDocs.length }}</span>
      </div>
      <div class="ic-grid">
        <article
          v-for="doc in pinnedDocs"
          :key="'p-' + doc.id"
          class="ic-card ic-card--pinned"
          @click="openDocument(doc)"
        >
          <div class="ic-card__top">
            <span class="ic-card__icon" :style="{ background: categoryGradient(doc.category) }">
              {{ doc.icon || categoryIcon(doc.category) }}
            </span>
            <span class="ic-card__pin" title="Pinned">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.39 4.84L20 7.62l-3.91 3.81.92 5.37L12 14.27 6.99 16.8l.92-5.37L4 7.62l5.61-.78L12 2z"/></svg>
            </span>
            <button type="button" class="ic-card__menu" :class="{ 'is-open': openMenuId === doc.id }" @click.stop="toggleMenu(doc)" aria-label="More actions">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
            </button>
            <div v-if="openMenuId === doc.id" class="ic-card__menu-pop" @click.stop>
              <button type="button" class="ic-menu-item" @click="openDocument(doc)">Open</button>
              <button type="button" class="ic-menu-item" @click="downloadPdf(doc)">Download as PDF</button>
              <button v-if="canEdit" type="button" class="ic-menu-item" @click="editDocument(doc)">Edit</button>
              <div v-if="canEdit" class="ic-menu-sep"></div>
              <button v-if="canEdit" type="button" class="ic-menu-item ic-menu-item--danger" @click="confirmDelete(doc)">Delete</button>
            </div>
          </div>
          <h3 class="ic-card__title">{{ doc.title }}</h3>
          <p class="ic-card__summary">{{ doc.summary || 'No summary provided.' }}</p>
          <div class="ic-card__meta">
            <span class="ic-card__pill" :class="categoryPillClass(doc.category)">{{ categoryLabel(doc.category) }}</span>
            <span v-if="doc.status !== 'published'" class="ic-card__pill ic-pill--gray">{{ formatStatus(doc.status) }}</span>
            <span class="ic-card__date">{{ relativeDate(doc.published_at || doc.updated_at) }}</span>
          </div>
        </article>
      </div>
    </section>

    <!-- Main results section -->
    <section class="ic-section">
      <div class="ic-section__head">
        <span class="ic-section__icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        </span>
        <h2>{{ activeCategory === 'all' ? 'All Documents' : categoryLabel(activeCategory) }}</h2>
        <span class="ic-section__count">{{ resultsTotal }}</span>
      </div>

      <!-- Loading: card-shaped skeletons regardless of view mode for visual consistency -->
      <div v-if="isLoading" class="ic-grid">
        <div v-for="i in 6" :key="'skel-' + i" class="ic-card ic-card--skeleton" aria-hidden="true">
          <div class="ic-skel ic-skel--icon"></div>
          <div class="ic-skel ic-skel--title"></div>
          <div class="ic-skel ic-skel--line"></div>
          <div class="ic-skel ic-skel--line short"></div>
        </div>
      </div>

      <div v-else-if="resultsTotal === 0" class="ic-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="9" y1="13" x2="15" y2="13"/>
          <line x1="9" y1="17" x2="13" y2="17"/>
        </svg>
        <h3>No documents found</h3>
        <p>{{ search ? 'Try a different search term or category.' : 'Nothing has been published yet for this filter.' }}</p>
        <button v-if="canEdit && !search" type="button" class="ic-btn ic-btn--primary" @click="goToCreate">Create the first document</button>
      </div>

      <!-- Card view -->
      <div v-else-if="viewMode === 'grid'" class="ic-grid">
        <article
          v-for="doc in visibleDocs"
          :key="doc.id"
          class="ic-card"
          @click="openDocument(doc)"
        >
          <div class="ic-card__top">
            <span class="ic-card__icon" :style="{ background: categoryGradient(doc.category) }">
              {{ doc.icon || categoryIcon(doc.category) }}
            </span>
            <span v-if="doc.pinned" class="ic-card__pin" title="Pinned">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.39 4.84L20 7.62l-3.91 3.81.92 5.37L12 14.27 6.99 16.8l.92-5.37L4 7.62l5.61-.78L12 2z"/></svg>
            </span>
            <button type="button" class="ic-card__menu" :class="{ 'is-open': openMenuId === doc.id }" @click.stop="toggleMenu(doc)" aria-label="More actions">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
            </button>
            <div v-if="openMenuId === doc.id" class="ic-card__menu-pop" @click.stop>
              <button type="button" class="ic-menu-item" @click="openDocument(doc)">Open</button>
              <button type="button" class="ic-menu-item" @click="downloadPdf(doc)">Download as PDF</button>
              <button v-if="canEdit" type="button" class="ic-menu-item" @click="editDocument(doc)">Edit</button>
              <div v-if="canEdit" class="ic-menu-sep"></div>
              <button v-if="canEdit" type="button" class="ic-menu-item ic-menu-item--danger" @click="confirmDelete(doc)">Delete</button>
            </div>
          </div>
          <h3 class="ic-card__title">{{ doc.title }}</h3>
          <p class="ic-card__summary">{{ doc.summary || 'No summary provided.' }}</p>
          <div class="ic-card__meta">
            <span class="ic-card__pill" :class="categoryPillClass(doc.category)">{{ categoryLabel(doc.category) }}</span>
            <span v-if="doc.status !== 'published'" class="ic-card__pill ic-pill--gray">{{ formatStatus(doc.status) }}</span>
            <span class="ic-card__date">{{ relativeDate(doc.published_at || doc.updated_at) }}</span>
          </div>
          <div v-if="audienceTags(doc).length || orgTags(doc).length" class="ic-card__audience">
            <span
              v-for="role in audienceTags(doc)"
              :key="'r-' + role"
              class="ic-audience-tag"
            >{{ formatRole(role) }}</span>
            <span
              v-for="org in orgTags(doc)"
              :key="'o-' + org.id"
              class="ic-audience-tag ic-audience-tag--org"
              :title="org.name"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 21V7l9-4 9 4v14"/><path d="M9 21V11h6v10"/></svg>
              {{ org.name }}
            </span>
          </div>
        </article>
      </div>

      <!-- List view -->
      <div v-else class="ic-list">
        <header class="ic-list__head">
          <span class="ic-list__col ic-list__col--title">Document</span>
          <span class="ic-list__col ic-list__col--cat">Category</span>
          <span class="ic-list__col ic-list__col--orgs">Organisation</span>
          <span class="ic-list__col ic-list__col--audience">Audience</span>
          <span v-if="canEdit" class="ic-list__col ic-list__col--status">Status</span>
          <span class="ic-list__col ic-list__col--date">Updated</span>
          <span class="ic-list__col ic-list__col--actions"></span>
        </header>
        <div
          v-for="doc in listDocs"
          :key="'l-' + doc.id"
          class="ic-row"
          @click="openDocument(doc)"
        >
          <div class="ic-list__col ic-list__col--title">
            <span class="ic-row__icon" :style="{ background: categoryGradient(doc.category) }">
              {{ doc.icon || categoryIcon(doc.category) }}
            </span>
            <div class="ic-row__title-wrap">
              <span class="ic-row__title">
                {{ doc.title }}
                <svg v-if="doc.pinned" class="ic-row__pin" width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.39 4.84L20 7.62l-3.91 3.81.92 5.37L12 14.27 6.99 16.8l.92-5.37L4 7.62l5.61-.78L12 2z"/></svg>
              </span>
              <span class="ic-row__summary">{{ doc.summary || '—' }}</span>
            </div>
          </div>
          <div class="ic-list__col ic-list__col--cat">
            <span class="ic-card__pill" :class="categoryPillClass(doc.category)">{{ categoryLabel(doc.category) }}</span>
          </div>
          <div class="ic-list__col ic-list__col--orgs">
            <template v-if="orgTags(doc).length">
              <span
                v-for="org in orgTags(doc)"
                :key="'lo-' + doc.id + '-' + org.id"
                class="ic-audience-tag ic-audience-tag--org"
              >{{ org.name }}</span>
            </template>
            <span v-else class="ic-row__muted">All orgs</span>
          </div>
          <div class="ic-list__col ic-list__col--audience">
            <template v-if="audienceTags(doc).length">
              <span
                v-for="role in audienceTags(doc)"
                :key="'lr-' + doc.id + '-' + role"
                class="ic-audience-tag"
              >{{ formatRole(role) }}</span>
            </template>
            <span v-else class="ic-row__muted">Everyone</span>
          </div>
          <div v-if="canEdit" class="ic-list__col ic-list__col--status">
            <span class="ic-card__pill" :class="statusPillClass(doc.status)">{{ formatStatus(doc.status) }}</span>
          </div>
          <div class="ic-list__col ic-list__col--date">{{ relativeDate(doc.published_at || doc.updated_at) }}</div>
          <div class="ic-list__col ic-list__col--actions" @click.stop>
            <button type="button" class="ic-row-btn" title="Download PDF" @click="downloadPdf(doc)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </button>
            <button v-if="canEdit" type="button" class="ic-row-btn" title="Edit" @click="editDocument(doc)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
            <button v-if="canEdit" type="button" class="ic-row-btn ic-row-btn--danger" title="Delete" @click="confirmDelete(doc)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
            </button>
          </div>
        </div>
      </div>
    </section>

    <AppConfirmDialog
      :visible="!!docToDelete"
      title="Delete this document?"
      :message="deleteMessage"
      confirm-label="Delete"
      variant="danger"
      :busy="isDeleting"
      busy-label="Deleting…"
      @cancel="docToDelete = null"
      @confirm="deleteDocument"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import { openPrintWindow } from './printDocument'

const VIEW_MODE_KEY = 'info_center_view_mode'

const CATEGORY_META = {
  policy:      { label: 'Policy',       icon: '📋', gradient: 'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)', pill: 'ic-pill--green' },
  sop:         { label: 'SOP',          icon: '⚙️', gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', pill: 'ic-pill--blue' },
  guide:       { label: 'Guide',        icon: '📘', gradient: 'linear-gradient(135deg, #6d28d9 0%, #a78bfa 100%)', pill: 'ic-pill--violet' },
  announcement:{ label: 'Announcement', icon: '📣', gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)', pill: 'ic-pill--amber' },
  faq:         { label: 'FAQ',          icon: '❓', gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', pill: 'ic-pill--teal' },
  training:    { label: 'Training',     icon: '🎓', gradient: 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)', pill: 'ic-pill--pink' },
  compliance:  { label: 'Compliance',   icon: '🛡️', gradient: 'linear-gradient(135deg, #991b1b 0%, #ef4444 100%)', pill: 'ic-pill--red' },
  benefits:    { label: 'Benefits',     icon: '💼', gradient: 'linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)', pill: 'ic-pill--cyan' }
}

function readStoredRoles () {
  // The router stores comma-joined role names in localStorage on login.
  // Falling back to the auth_user payload covers a stale cache path.
  const raw = localStorage.getItem('roles')
  if (raw && typeof raw === 'string') {
    return raw.split(',').map((r) => r.trim().toLowerCase()).filter(Boolean)
  }
  try {
    const u = JSON.parse(localStorage.getItem('auth_user') || 'null')
    if (u && Array.isArray(u.roles)) return u.roles.map((r) => String(r).trim().toLowerCase())
    if (u && typeof u.roles === 'string') return u.roles.split(',').map((r) => r.trim().toLowerCase())
  } catch (_) { /* ignore */ }
  return []
}

export default {
  name: 'InfoCenter',
  components: { AppConfirmDialog, AppDropdown },
  data () {
    let storedView = 'grid'
    try { storedView = localStorage.getItem(VIEW_MODE_KEY) || 'grid' } catch (_) { /* private mode */ }
    return {
      documents: [],
      organisations: [],
      isLoading: false,
      errorMessage: '',
      search: '',
      activeCategory: 'all',
      statusFilter: 'all',
      orgFilter: 0,
      viewMode: storedView === 'list' ? 'list' : 'grid',
      canEdit: false,
      openMenuId: null,
      docToDelete: null,
      isDeleting: false,
      availableCategories: Object.keys(CATEGORY_META),
      statusOptions: [
        { value: 'all', label: 'All' },
        { value: 'published', label: 'Published' },
        { value: 'draft', label: 'Drafts' },
        { value: 'archived', label: 'Archived' }
      ]
    }
  },
  computed: {
    totalCount () {
      return this.statusFilteredDocs.length
    },
    pinnedCount () {
      return this.documents.filter((d) => d.pinned && (d.status === 'published' || this.canEdit)).length
    },
    draftCount () {
      return this.documents.filter((d) => d.status === 'draft').length
    },
    statusFilteredDocs () {
      let docs = this.documents
      if (this.canEdit && this.orgFilter) {
        // Admin-only org filter — only restrict if a specific org is
        // picked. "All organisations" (orgFilter=0) keeps the full set.
        docs = docs.filter((d) => {
          const ids = Array.isArray(d.audience_organisations) ? d.audience_organisations.map((i) => Number(i)) : []
          // Empty list = visible everywhere, so any active org filter
          // should still surface those documents.
          return ids.length === 0 || ids.includes(Number(this.orgFilter))
        })
      }
      if (!this.canEdit) return docs
      if (this.statusFilter === 'all') return docs
      return docs.filter((d) => d.status === this.statusFilter)
    },
    categoryList () {
      const counts = {}
      for (const d of this.statusFilteredDocs) {
        counts[d.category] = (counts[d.category] || 0) + 1
      }
      return this.availableCategories
        .filter((key) => counts[key])
        .map((key) => ({
          key,
          label: this.categoryLabel(key),
          count: counts[key]
        }))
    },
    pinnedDocs () {
      if (this.activeCategory !== 'all' || this.search) return []
      return this.statusFilteredDocs.filter((d) => d.pinned)
    },
    visibleDocs () {
      const term = this.search.trim().toLowerCase()
      const onAllView = this.activeCategory === 'all'
      return this.statusFilteredDocs.filter((d) => {
        if (!onAllView && d.category !== this.activeCategory) return false
        // On the "All" view (grid mode only) with no active search,
        // pinned docs already appear in the dedicated Pinned section —
        // hide them here to avoid duplication. List view always shows
        // everything in one stream so the pinned flag becomes a chip.
        if (this.viewMode === 'grid' && onAllView && !term && d.pinned) return false
        if (!term) return true
        return (
          (d.title || '').toLowerCase().includes(term) ||
          (d.summary || '').toLowerCase().includes(term)
        )
      })
    },
    listDocs () {
      // List view doesn't use a separate "Pinned" section, so we render
      // the entire filtered set in one go (pinned-first for hierarchy).
      const term = this.search.trim().toLowerCase()
      const onAllView = this.activeCategory === 'all'
      return this.statusFilteredDocs
        .filter((d) => {
          if (!onAllView && d.category !== this.activeCategory) return false
          if (!term) return true
          return (
            (d.title || '').toLowerCase().includes(term) ||
            (d.summary || '').toLowerCase().includes(term)
          )
        })
        .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0))
    },
    resultsTotal () {
      return this.viewMode === 'list'
        ? this.listDocs.length
        : this.visibleDocs.length + (this.activeCategory === 'all' && !this.search ? this.pinnedDocs.length : 0)
    },
    deleteMessage () {
      return this.docToDelete ? `“${this.docToDelete.title}” will be permanently removed.` : ''
    },
    orgFilterOptions () {
      // 0 acts as the "no filter" sentinel because AppDropdown's value
      // prop doesn't accept null cleanly. The change handler converts
      // it back to plain integer ids when piping into the listing
      // filter. Mirrors the pattern used by SettingsDiscountPresets.
      return [
        { value: 0, label: 'All organisations' },
        ...this.organisations.map((o) => ({ value: Number(o.id), label: o.name }))
      ]
    }
  },
  watch: {
    statusFilter () {
      // Refetch when the editor toggles status filter so the server
      // can include drafts/archived for editors.
      this.loadDocuments()
    },
    '$store.state.activeOrganisationId' () {
      // Org switch from the sidebar — refetch so the org-scoped audience
      // filter on the server picks the new context.
      this.loadDocuments()
    }
  },
  created () {
    this.loadDocuments()
  },
  mounted () {
    document.addEventListener('mousedown', this.handleDocClick)
    document.addEventListener('keydown', this.handleEsc)
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.handleDocClick)
    document.removeEventListener('keydown', this.handleEsc)
  },
  activated () {
    // Called when the keep-alive'd listing is revisited (e.g. after
    // publishing a doc and bouncing back from /info-center/:id/edit).
    // We refetch unconditionally so the new / updated doc shows up
    // without the user needing to hit Refresh.
    this.loadDocuments()
  },
  methods: {
    loadDocuments () {
      this.isLoading = true
      this.errorMessage = ''
      const params = {}
      if (this.canEdit && this.statusFilter !== 'all') {
        params.status = this.statusFilter
      }
      const activeOrgId = this.$store && this.$store.state && this.$store.state.activeOrganisationId
      if (activeOrgId) {
        params.organisation_id = activeOrgId
      }
      axios.get(`${this.$apiAdress}/api/info-documents`, { params })
        .then((response) => {
          const payload = response.data || {}
          this.documents = Array.isArray(payload.data) ? payload.data : []
          this.canEdit = Boolean(payload.is_editor)
          if (Array.isArray(payload.categories) && payload.categories.length) {
            this.availableCategories = payload.categories
          }
          if (Array.isArray(payload.organisations)) {
            this.organisations = payload.organisations
          }
        })
        .catch((error) => {
          // Surface message but stay quiet about 401 — the global axios
          // interceptor handles auth redirects elsewhere in the app.
          if (error?.response?.status !== 401) {
            this.errorMessage = error?.response?.data?.message || 'Unable to load documents.'
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    goToCreate () {
      this.$router.push({ path: '/info-center/new' })
    },
    openDocument (doc) {
      this.$router.push({ path: `/info-center/${doc.slug}` })
    },
    editDocument (doc) {
      this.openMenuId = null
      this.$router.push({ path: `/info-center/${doc.id}/edit` })
    },
    setViewMode (mode) {
      this.viewMode = mode
      try { localStorage.setItem(VIEW_MODE_KEY, mode) } catch (_) { /* ignore */ }
    },
    toggleMenu (doc) {
      this.openMenuId = this.openMenuId === doc.id ? null : doc.id
    },
    closeMenu () {
      this.openMenuId = null
    },
    handleDocClick (event) {
      if (this.openMenuId === null) return
      const tgt = event.target
      // Keep open if the click hit the menu or its trigger; otherwise
      // dismiss. We rely on the .stop modifier on the trigger to
      // prevent toggle/close races.
      if (tgt && tgt.closest && (tgt.closest('.ic-card__menu-pop') || tgt.closest('.ic-card__menu'))) return
      this.openMenuId = null
    },
    handleEsc (event) {
      if (event.key === 'Escape') {
        if (this.openMenuId !== null) this.openMenuId = null
        if (this.docToDelete) this.docToDelete = null
      }
    },
    confirmDelete (doc) {
      this.openMenuId = null
      this.docToDelete = doc
    },
    deleteDocument () {
      if (!this.docToDelete) return
      const id = this.docToDelete.id
      this.isDeleting = true
      axios.delete(`${this.$apiAdress}/api/info-documents/${id}`)
        .then(() => {
          this.documents = this.documents.filter((d) => d.id !== id)
          this.docToDelete = null
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to delete document.'
        })
        .finally(() => {
          this.isDeleting = false
        })
    },
    statusPillClass (status) {
      if (status === 'published') return 'ic-pill--green'
      if (status === 'draft') return 'ic-pill--amber'
      return 'ic-pill--gray'
    },
    // Fetch the full document then hand off to the shared print helper
    // — same flow used from the reader page so the PDF output stays in
    // sync no matter where the user invokes the download.
    downloadPdf (doc) {
      this.openMenuId = null
      axios.get(`${this.$apiAdress}/api/info-documents/${doc.id}`)
        .then((response) => {
          const reason = openPrintWindow(response.data || {})
          if (reason) this.errorMessage = reason
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to load document for download.'
        })
    },
    categoryLabel (key) {
      return CATEGORY_META[key]?.label || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '—')
    },
    categoryIcon (key) {
      if (key === 'all') return '✨'
      return CATEGORY_META[key]?.icon || '📄'
    },
    categoryGradient (key) {
      return CATEGORY_META[key]?.gradient || 'linear-gradient(135deg, #475569 0%, #64748b 100%)'
    },
    categoryPillClass (key) {
      return CATEGORY_META[key]?.pill || 'ic-pill--gray'
    },
    formatStatus (status) {
      if (!status) return ''
      return status.charAt(0).toUpperCase() + status.slice(1)
    },
    formatRole (role) {
      if (!role) return ''
      if (role === 'all') return 'Everyone'
      return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    audienceTags (doc) {
      const roles = Array.isArray(doc.audience_roles) ? doc.audience_roles : []
      if (!roles.length) return []
      // Show at most 3 tags + an overflow chip; the reader page lists
      // the full set so we can keep the card compact.
      return roles.slice(0, 3)
    },
    orgTags (doc) {
      const ids = Array.isArray(doc.audience_organisations) ? doc.audience_organisations : []
      if (!ids.length) return []
      // Resolve ids → { id, name } using the orgs list returned by the
      // server. Fall back to a "Org #N" label so a missing record never
      // erases the scope from the card.
      const byId = {}
      for (const o of this.organisations) byId[Number(o.id)] = o.name
      return ids.slice(0, 2).map((id) => ({ id: Number(id), name: byId[Number(id)] || `Org #${id}` }))
    },
    relativeDate (raw) {
      if (!raw) return ''
      const date = new Date(raw)
      if (Number.isNaN(date.getTime())) return ''
      const diff = (Date.now() - date.getTime()) / 1000
      if (diff < 60) return 'Just now'
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
      if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    },
    storedRoles () { return readStoredRoles() }
  }
}
</script>

<style scoped>
.ic-page { padding: 0.25rem 0 2rem; }

/* ─── Toolbar ─────────────────────────────────────────────────────── */
.ic-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
  flex-wrap: wrap;
}

.ic-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }

.ic-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.ic-toolbar h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.ic-toolbar p { margin: 0; font-size: 0.78rem; color: #6b7280; }

.ic-toolbar__actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }

.ic-alert { margin: 0 0 0.75rem; border-radius: 10px; }

/* ─── Buttons ─────────────────────────────────────────────────────── */
.ic-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 0.95rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.ic-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}
.ic-btn--primary:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28); }

.ic-btn--ghost {
  background: #ffffff;
  border-color: #e5e9f2;
  color: #1f2a44;
}
.ic-btn--ghost:hover { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }

.ic-btn:focus { outline: none; }
.ic-btn::-moz-focus-inner { border: 0; }
.ic-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.ic-refresh__icon { transition: transform 0.3s ease; }
.ic-refresh:hover .ic-refresh__icon { transform: rotate(60deg); }
.ic-refresh.is-loading .ic-refresh__icon { animation: ic-spin 0.9s linear infinite; }
@keyframes ic-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* ─── Compact search / status bar ─────────────────────────────────── */
.ic-bar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0 0 0.7rem;
  flex-wrap: wrap;
}

.ic-search {
  position: relative;
  flex: 1 1 320px;
  max-width: 420px;
  min-width: 240px;
}

.ic-search__icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.ic-search__input {
  width: 100%;
  height: 36px;
  padding: 0 2.1rem 0 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #1f2a44;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ic-search__input::placeholder { color: #94a3b8; }
.ic-search__input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.ic-search__clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.ic-search__clear:hover { background: #f1f5f9; color: #475569; }
.ic-search__clear:focus { outline: none; }
.ic-search__clear:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

/* ─── Category chip strip ─────────────────────────────────────────── */
.ic-chipbar-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.ic-stat-line {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #64748b;
  white-space: nowrap;
}
.ic-stat-line strong { color: #0e5b46; font-weight: 700; }
.ic-stat-line__dot { color: #cbd5e1; }

.ic-chipbar {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  padding: 0.3rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}
.ic-chipbar::-webkit-scrollbar { display: none; }

.ic-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 32px;
  padding: 0 0.85rem;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
}
.ic-chip:hover { background: #f1f5f9; color: #334155; }
.ic-chip.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
}
.ic-chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 0.3rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  color: inherit;
  font-size: 0.68rem;
  font-weight: 700;
}
.ic-chip.is-active .ic-chip__count {
  background: rgba(255, 255, 255, 0.25);
}
.ic-chip__icon { font-size: 0.95rem; line-height: 1; }
.ic-chip:focus { outline: none; }
.ic-chip:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

/* AppDropdown owns its own chrome; we just give it a sensible width
   inside the filter row so it doesn't stretch the bar full-width. */
.ic-org-filter {
  flex: 0 1 240px;
  min-width: 200px;
}

.ic-view-toggle {
  display: inline-flex;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  padding: 2px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.ic-view-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #64748b;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.ic-view-btn:hover { color: #1f2a44; background: #f1f5f9; }
.ic-view-btn.is-active { background: #0e5b46; color: #ffffff; }
.ic-view-btn:focus { outline: none; }
.ic-view-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.ic-status-toggle {
  display: inline-flex;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  padding: 2px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.ic-status-btn {
  border: none;
  background: transparent;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  font-size: 0.74rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.ic-status-btn:hover { color: #1f2a44; }
.ic-status-btn.is-active { background: #0e5b46; color: #ffffff; }
.ic-status-btn:focus { outline: none; }
.ic-status-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

/* ─── Section ─────────────────────────────────────────────────────── */
.ic-section { margin-bottom: 1.5rem; }

.ic-section__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.85rem;
  padding-left: 0.1rem;
}
.ic-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}
.ic-section__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
}
.ic-section__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 700;
}

/* ─── Cards grid ──────────────────────────────────────────────────── */
.ic-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.85rem;
}

.ic-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 14px;
  padding: 1rem 1.05rem 0.95rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-height: 175px;
}

.ic-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(14, 91, 70, 0.10), 0 2px 4px rgba(15, 23, 42, 0.04);
  border-color: #c8e8d9;
}

.ic-card--pinned {
  background: linear-gradient(180deg, #ffffff 0%, #f5fbf8 100%);
  border-color: #c8e8d9;
}

.ic-card__top { display: flex; align-items: center; justify-content: space-between; }

.ic-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  color: #ffffff;
  font-size: 1.1rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  box-shadow: 0 4px 10px rgba(14, 91, 70, 0.22);
}

.ic-card__pin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #fef3c7;
  color: #b45309;
}

.ic-card__menu {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: auto;
  border: none;
  background: transparent;
  color: #94a3b8;
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.ic-card:hover .ic-card__menu,
.ic-card__menu.is-open { opacity: 1; }
.ic-card__menu:hover { color: #1f2a44; background: #f1f5f9; }
.ic-card__menu:focus { outline: none; opacity: 1; }
.ic-card__menu:focus-visible { outline: none; box-shadow: 0 0 0 2px rgba(22, 160, 122, 0.3); }

.ic-card__menu-pop {
  position: absolute;
  top: 38px;
  right: 12px;
  z-index: 12;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.18);
  padding: 0.3rem;
  min-width: 168px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.ic-menu-item {
  display: block;
  padding: 0.45rem 0.6rem;
  border: none;
  background: transparent;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  border-radius: 6px;
}
.ic-menu-item:hover { background: #f1f5f9; color: #0e5b46; }
.ic-menu-item--danger { color: #b91c1c; }
.ic-menu-item--danger:hover { background: #fef2f2; color: #991b1b; }
.ic-menu-item:focus { outline: none; }
.ic-menu-item:focus-visible { outline: none; box-shadow: 0 0 0 2px rgba(22, 160, 122, 0.3); }
.ic-menu-sep { height: 1px; background: #eef1f7; margin: 0.2rem 0.15rem; }

.ic-card__title {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ic-card__summary {
  margin: 0;
  font-size: 0.82rem;
  color: #475569;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ic-card__meta {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: auto;
  flex-wrap: wrap;
}

.ic-card__pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: 0.02em;
}

.ic-pill--green  { background: #dff5ea; color: #0e5b46; }
.ic-pill--blue   { background: #dbeafe; color: #1e40af; }
.ic-pill--amber  { background: #fef3c7; color: #92400e; }
.ic-pill--violet { background: #ede9fe; color: #5b21b6; }
.ic-pill--teal   { background: #ccfbf1; color: #115e59; }
.ic-pill--pink   { background: #fce7f3; color: #9d174d; }
.ic-pill--red    { background: #fee2e2; color: #991b1b; }
.ic-pill--cyan   { background: #cffafe; color: #155e75; }
.ic-pill--gray   { background: #f1f5f9; color: #475569; }

.ic-card__date { margin-left: auto; font-size: 0.72rem; color: #94a3b8; font-weight: 500; }

.ic-card__audience {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
  padding-top: 0.5rem;
  border-top: 1px dashed #eef1f7;
}

.ic-audience-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.5rem;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: capitalize;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ic-audience-tag--org {
  background: #e6f4ee;
  color: #0e5b46;
}
.ic-audience-tag--org svg { flex-shrink: 0; }

/* ─── Skeletons & empty ───────────────────────────────────────────── */
.ic-card--skeleton { cursor: default; pointer-events: none; }
.ic-card--skeleton:hover { transform: none; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); border-color: #e5e9f2; }

.ic-skel {
  background: linear-gradient(90deg, #f1f5f9 25%, #e5e9f2 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ic-skel-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}
.ic-skel--icon { width: 38px; height: 38px; border-radius: 10px; }
.ic-skel--title { width: 80%; height: 14px; }
.ic-skel--line { width: 100%; height: 10px; }
.ic-skel--line.short { width: 60%; }
@keyframes ic-skel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.ic-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem 1.25rem;
  background: #ffffff;
  border: 1px dashed #e5e9f2;
  border-radius: 14px;
  color: #64748b;
}
.ic-empty svg { color: #cbd5e1; margin-bottom: 0.75rem; }
.ic-empty h3 { margin: 0 0 0.25rem; font-size: 1rem; color: #1f2a44; }
.ic-empty p { margin: 0 0 1rem; font-size: 0.82rem; }

/* ─── List view ──────────────────────────────────────────────────── */
.ic-list {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.ic-list__head {
  display: grid;
  grid-template-columns: minmax(0, 2fr) 110px minmax(0, 1.2fr) minmax(0, 1.2fr) 100px 100px 100px;
  gap: 0.6rem;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.ic-list__head .ic-list__col--actions { text-align: right; }

.ic-row {
  display: grid;
  grid-template-columns: minmax(0, 2fr) 110px minmax(0, 1.2fr) minmax(0, 1.2fr) 100px 100px 100px;
  gap: 0.6rem;
  align-items: center;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.12s ease;
}
.ic-row:last-child { border-bottom: none; }
.ic-row:hover { background: #f8fffb; }

.ic-list__col { font-size: 0.82rem; color: #1f2a44; min-width: 0; }
.ic-list__col--title { display: flex; align-items: center; gap: 0.7rem; min-width: 0; }
.ic-list__col--date { color: #64748b; font-size: 0.75rem; }
.ic-list__col--actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
}

.ic-row__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.9rem;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.ic-row__title-wrap {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.ic-row__title {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ic-row__pin { color: #b45309; flex-shrink: 0; }
.ic-row__summary {
  font-size: 0.74rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ic-row__muted { font-size: 0.75rem; color: #94a3b8; }

.ic-list__col--orgs,
.ic-list__col--audience {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  overflow: hidden;
}

.ic-row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: border-color 0.12s ease, color 0.12s ease, background 0.12s ease;
}
.ic-row-btn:hover { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.ic-row-btn--danger:hover { border-color: #ef4444; color: #b91c1c; background: #fef2f2; }
.ic-row-btn:focus { outline: none; }
.ic-row-btn:focus-visible { outline: none; box-shadow: 0 0 0 2px rgba(22, 160, 122, 0.3); }

@media (max-width: 960px) {
  .ic-list__head, .ic-row {
    grid-template-columns: minmax(0, 2fr) 100px minmax(0, 1fr) 90px 100px;
  }
  .ic-list__col--audience, .ic-list__col--status { display: none; }
}

@media (max-width: 640px) {
  .ic-search { max-width: 100%; }
  .ic-list__head, .ic-row {
    grid-template-columns: minmax(0, 1fr) 90px 90px;
  }
  .ic-list__col--orgs, .ic-list__col--audience, .ic-list__col--status, .ic-list__col--cat { display: none; }
}
</style>
